import apiClient from './apiClient';
import { API_URL } from '../config';

export interface PatientContextInput {
  first_name?: string;
  last_name?: string;
  mrn?: string;
  dob?: string;
  gender?: string;
  accession_number?: string;
  modality?: string;
  study_date?: string;
  body_part?: string;
  // PACS-02: Additional DICOM metadata
  study_description?: string;
  clinical_indication?: string;
  study_priority?: string;
  laterality?: string;
  ordering_physician_name?: string;
}

export interface PatientResponse {
  id: string;
  patient_hash_id: string;
  first_name?: string;
  last_name?: string;
  dob?: string;
  mrn?: string;
  gender?: string;
  created_at?: string;
  // Study-specific fields
  accession_number?: string;
  modality?: string;
  study_date?: string;
  body_part?: string;
  // PACS-02: Additional DICOM metadata
  study_description?: string;
  clinical_indication?: string;
  study_priority?: string;
  laterality?: string;
  ordering_physician_name?: string;
}

export interface ReportHistoryEntry {
  report_id: number;
  session_id: string | null;
  study_date: string | null;
  diagnosis_category: string | null;
  pathology_type: string | null;
  anatomy_region: string | null;
  modality: string | null;
  report_summary: Record<string, unknown> | null;
  created_at: string | null;
}

export interface ReportHistoryListResponse {
  patient_hash_id: string;
  total_reports: number;
  reports: ReportHistoryEntry[];
}

export interface PatientSearchResult {
  patient_id: string;
  display_name: string;
  dob: string | null;
  mrn: string | null;
  gender: string | null;
  report_count: number;
}

export interface PatientSearchResponse {
  patients: PatientSearchResult[];
  total: number;
}

export interface PatientSearchParams {
  mrn?: string;
  phone?: string;
  email?: string;
  name?: string;
  limit?: number;
}

export interface PatientReportEntry {
  report_id: number;
  study_date: string | null;
  modality: string | null;
  diagnosis_category: string | null;
  pathology_type: string | null;
  status: string | null;
  anatomy_region: string | null;
  session_id: string | null;
  created_at: string | null;
}

export interface PatientReportsResponse {
  patient_id: string;
  display_name: string;
  reports: PatientReportEntry[];
  total: number;
}

const createOrUpdatePatient = async (data: {
  mrn: string;
  dob: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
}): Promise<PatientResponse> => {
  const response = await apiClient.post(`${API_URL}/patients`, data);
  return response.data;
};

const getPatient = async (patientId: string): Promise<PatientResponse> => {
  const response = await apiClient.get(`${API_URL}/patients/${patientId}`);
  return response.data;
};

const lookupPatient = async (mrn: string, dob: string): Promise<PatientResponse> => {
  const response = await apiClient.post(`${API_URL}/patients/lookup`, { mrn, dob });
  return response.data;
};

const getPatientHistory = async (
  patientId: string,
  params?: { anatomy?: string; modality?: string; limit?: number }
): Promise<ReportHistoryListResponse> => {
  const response = await apiClient.get(`${API_URL}/patients/${patientId}/history`, { params });
  return response.data;
};

const getSessionPatient = async (sessionId: string): Promise<PatientResponse> => {
  const response = await apiClient.get(`${API_URL}/sessions/${sessionId}/patient`);
  return response.data;
};

const searchPatients = async (params: PatientSearchParams): Promise<PatientSearchResponse> => {
  const response = await apiClient.get(`${API_URL}/patients/search`, { params });
  return response.data;
};

const getPatientReports = async (
  patientId: string,
  params?: { modality?: string; anatomy?: string; limit?: number }
): Promise<PatientReportsResponse> => {
  const response = await apiClient.get(`${API_URL}/patients/${patientId}/reports`, { params });
  return response.data;
};

export interface PatientHistorySummaryEntry {
  date: string | null;
  modality: string | null;
  body_part: string | null;
  summary: string;
  session_id: string | null;
  report_status: string | null;
}

export interface PatientHistorySummaryResponse {
  patient_id: string;
  history: PatientHistorySummaryEntry[];
  total: number;
}

const getPatientHistorySummary = async (
  patientId: string,
  limit = 5,
  beforeReportId?: number
): Promise<PatientHistorySummaryResponse> => {
  const params: Record<string, number> = { limit };
  if (beforeReportId) params.before_report_id = beforeReportId;
  const response = await apiClient.get(`${API_URL}/patients/${patientId}/history-summary`, {
    params,
  });
  return response.data;
};

export interface AttachPatientPayload {
  mrn: string;
  dob: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
  accession_number?: string;
  modality?: string;
  study_date?: string;
  body_part?: string;
}

export interface AttachPatientResponse {
  message: string;
  patient_id: string;
  patient_hash_id: string;
  session_id: string;
}

const attachPatientToTranscription = async (
  transcriptionId: number,
  payload: AttachPatientPayload
): Promise<AttachPatientResponse> => {
  const response = await apiClient.put(
    `${API_URL}/transcriptions/${transcriptionId}/attach-patient`,
    payload
  );
  return response.data;
};

const updateSessionPatient = async (
  sessionId: string,
  payload: PatientContextInput & { mrn?: string; dob?: string; first_name?: string; last_name?: string; gender?: string }
): Promise<PatientResponse> => {
  const response = await apiClient.post(
    `${API_URL}/sessions/${sessionId}/patient`,
    payload
  );
  return response.data;
};

const patientService = {
  createOrUpdatePatient,
  getPatient,
  lookupPatient,
  getPatientHistory,
  getSessionPatient,
  searchPatients,
  getPatientReports,
  getPatientHistorySummary,
  attachPatientToTranscription,
  updateSessionPatient,
};

export default patientService;
